import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import type { QuotationPdfFile } from '../../admin/admin.api';
import { Card, SectionTitle } from '../../shared/components';
import { uploadFile } from '../../shared/service/fileUpload.service';
import QuotationPdfViewer from '../../work-implementation/reports/QuotationPdfViewer';
import ScheduleVisitDialog from '../components/ScheduleVisitDialog';
import { OutcomeChip, ResultChip, VisitStatusChip } from '../components/VisitChips';
import {
  CheckResult,
  fetchVisitPdf,
  isImage,
  Outcome,
  OUTCOMES,
  projectLabel,
  ReportDraft,
  siteVisitsApi,
  VISIT_BY_LABELS,
  VisitCheck,
  VisitDetail,
  VisitFile,
} from '../siteVisits.api';
import { useSiteVisitMeta } from '../useSiteVisitMeta';

type NoteAction = 'cancel' | 'review' | 'sendBack' | null;

const NOTE_ACTIONS: Record<Exclude<NoteAction, null>, { title: string; label: string; button: string; required: boolean }> = {
  cancel: { title: 'Cancel this visit', label: 'Reason', button: 'Cancel visit', required: true },
  review: { title: 'Mark the report reviewed', label: 'Review note (optional)', button: 'Mark reviewed', required: false },
  sendBack: { title: 'Send back to the engineer', label: 'What needs to change', button: 'Send back', required: true },
};

const fmtDateTime = (d: string | null) => (d ? dayjs(d).format('DD MMM YYYY, hh:mm A') : '—');

/**
 * One site visit: what was planned, what the engineer found (checklist, photos, report),
 * and what the office does next — edit, start on the engineer's behalf, cancel, review or send
 * back, and the PDF report for the client.
 */
export default function SiteVisitDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const meta = useSiteVisitMeta();
  const [visit, setVisit] = useState<VisitDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [noteAction, setNoteAction] = useState<NoteAction>(null);
  const [note, setNote] = useState('');
  const [pdf, setPdf] = useState<QuotationPdfFile | null>(null);
  const pdfUrl = useRef<string | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    siteVisitsApi
      .get(id)
      .then((v) => {
        setVisit(v);
        setError(null);
      })
      .catch((e) => setError(logError({ message: 'Could not load the visit', error: e, code: 'SITE_VISIT_LOAD_FAILED' })))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(
    () => () => {
      if (pdfUrl.current) URL.revokeObjectURL(pdfUrl.current);
    },
    []
  );

  const run = async (fn: () => Promise<VisitDetail>, done: string) => {
    setBusy(true);
    try {
      setVisit(await fn());
      toast.success(done);
      return true;
    } catch (e) {
      toast.error(logError({ message: 'That did not go through', error: e, code: 'SITE_VISIT_ACTION_FAILED' }));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const confirmNote = async () => {
    if (!visit || !noteAction) return;
    const text = note.trim();
    if (NOTE_ACTIONS[noteAction].required && !text) return;
    const ok =
      noteAction === 'cancel'
        ? await run(() => siteVisitsApi.cancel(visit.id, text), 'Visit cancelled')
        : noteAction === 'review'
          ? await run(() => siteVisitsApi.review(visit.id, text || undefined), 'Report marked reviewed')
          : await run(() => siteVisitsApi.sendBack(visit.id, text), 'Sent back to the engineer');
    if (ok) setNoteAction(null);
  };

  const viewPdf = async () => {
    if (!visit) return;
    setBusy(true);
    try {
      const file = await fetchVisitPdf(visit.id, visit.visitNo);
      if (pdfUrl.current) URL.revokeObjectURL(pdfUrl.current);
      pdfUrl.current = file.url;
      setPdf(file);
    } catch (e) {
      toast.error(logError({ message: 'Could not open the PDF', error: e, code: 'SITE_VISIT_PDF_FAILED' }));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !visit) return <LinearProgress />;
  if (error && !visit) return <Alert severity='error'>{error}</Alert>;
  if (!visit) return null;

  const v = visit;
  const generalPhotos = v.photos.filter((p) => p.checkId == null);
  const answered = v.checks.filter((c) => c.result).length;

  return (
    <Stack gap={3}>
      <SectionTitle
        title={`SITE VISIT ${v.visitNo}`}
        action={
          <Stack direction='row' gap={1} flexWrap='wrap'>
            <Button startIcon={<Icon icon='mdi:arrow-left' />} onClick={() => navigate('/site-visits')}>All visits</Button>
            {v.canEdit && (
              <Button variant='outlined' startIcon={<Icon icon='mdi:pencil-outline' />} disabled={busy} onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
            {v.canStart && (
              <Button variant='outlined' startIcon={<Icon icon='mdi:play-circle-outline' />} disabled={busy}
                onClick={() => run(() => siteVisitsApi.start(v.id), 'Visit started')}>
                Start
              </Button>
            )}
            {v.canCancel && (
              <Button color='error' variant='outlined' startIcon={<Icon icon='mdi:close-circle-outline' />} disabled={busy}
                onClick={() => { setNote(''); setNoteAction('cancel'); }}>
                Cancel visit
              </Button>
            )}
            {v.canReview && (
              <>
                <Button color='warning' variant='outlined' startIcon={<Icon icon='mdi:undo' />} disabled={busy}
                  onClick={() => { setNote(''); setNoteAction('sendBack'); }}>
                  Send back
                </Button>
                <Button color='success' variant='contained' startIcon={<Icon icon='mdi:check-decagram' />} disabled={busy}
                  onClick={() => { setNote(''); setNoteAction('review'); }}>
                  Mark reviewed
                </Button>
              </>
            )}
            {v.status !== 'CANCELLED' && (
              <Button variant='contained' startIcon={<Icon icon='mdi:file-pdf-box' />} disabled={busy} onClick={viewPdf}>
                PDF report
              </Button>
            )}
          </Stack>
        }
      />
      {busy && <LinearProgress />}

      <Card>
        <Stack direction='row' alignItems='center' gap={1.5} flexWrap='wrap' sx={{ mb: 2 }}>
          <Typography variant='h6'>{projectLabel(v.projectCode, v.projectName, v.projectLocation)}</Typography>
          <VisitStatusChip status={v.status} />
          <OutcomeChip outcome={v.outcome} label={v.outcomeLabel} />
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          <Field label='Site address'>{v.siteAddress || '—'}</Field>
          <Field label='Visit'>
            {dayjs(v.scheduledDate).format('ddd, DD MMM YYYY')} · {v.shiftLabel ?? v.shift}
          </Field>
          <Field label={VISIT_BY_LABELS[v.visitBy] ?? 'Engineer'}>
            {v.engineerName ?? '—'}
            {v.engineerPhone && <Typography variant='caption' color='text.secondary' display='block'>{v.engineerPhone}</Typography>}
          </Field>
          <Field label='Purpose'>{v.purpose || '—'}</Field>
          <Field label='Stages'>{v.stages || '—'}</Field>
          <Field label='Scheduled by'>
            {v.createdByName ?? '—'}
            <Typography variant='caption' color='text.secondary' display='block'>{fmtDateTime(v.createdAt)}</Typography>
          </Field>
          {v.contacts.length > 0 && (
            <Field label='Contacts at site'>
              {v.contacts.map((c) => (
                <Typography key={`${c.role}-${c.name}`} variant='body2'>
                  {c.role}: {c.name}{c.phone ? ` · ${c.phone}` : ''}
                </Typography>
              ))}
            </Field>
          )}
          {v.startedAt && <Field label='Started'>{fmtDateTime(v.startedAt)}</Field>}
          {v.submittedAt && <Field label='Submitted'>{fmtDateTime(v.submittedAt)}</Field>}
        </Box>
        {v.instructions && (
          <Box sx={{ mt: 2 }}>
            <Field label='Instructions for the engineer'>
              <Box sx={{ whiteSpace: 'pre-wrap' }}>{v.instructions}</Box>
            </Field>
          </Box>
        )}
        {v.attachments.length > 0 && (
          <Stack direction='row' gap={1} flexWrap='wrap' alignItems='center' sx={{ mt: 2 }}>
            <Typography variant='caption' color='text.secondary'>Attachments:</Typography>
            {v.attachments.map((a) => (
              <Chip key={a.id} size='small' icon={<Icon icon='mdi:file-outline' />} label={a.name ?? 'File'}
                component='a' href={a.url} target='_blank' rel='noreferrer' clickable />
            ))}
          </Stack>
        )}
        {v.cancelReason && <Alert severity='warning' sx={{ mt: 2 }}>Cancelled: {v.cancelReason}</Alert>}
        {v.reviewNote && (
          <Alert severity={v.status === 'REVIEWED' ? 'success' : 'info'} sx={{ mt: 2 }}>
            {v.status === 'REVIEWED' ? 'Reviewed' : 'Office note'}
            {v.reviewedByName ? ` by ${v.reviewedByName}` : ''}
            {v.reviewedAt ? ` on ${fmtDateTime(v.reviewedAt)}` : ''}: {v.reviewNote}
          </Alert>
        )}
      </Card>

      <Card sx={{ p: 0 }}>
        <Typography variant='subtitle1' fontWeight={600} sx={{ px: 2, pt: 2 }}>
          Checklist · {answered}/{v.checks.length} answered
          {v.issues > 0 && (
            <Typography component='span' sx={{ color: 'error.main', fontWeight: 700, ml: 1 }}>
              {v.issues} issue{v.issues === 1 ? '' : 's'}
            </Typography>
          )}
        </Typography>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }}>#</TableCell>
                <TableCell>Item</TableCell>
                <TableCell sx={{ width: v.canWork ? 190 : 100 }}>Result</TableCell>
                <TableCell>Remark</TableCell>
                <TableCell>Photos</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {v.checks.map((c, i) =>
                v.canWork ? (
                  <EditableCheckRow key={c.id} index={i} visitId={v.id} check={c} onSaved={load} />
                ) : (
                  <TableRow key={c.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>
                      <Typography variant='body2'>{c.title}</Typography>
                      {c.hint && <Typography variant='caption' color='text.secondary'>{c.hint}</Typography>}
                      {c.addedOnSite && <Chip size='small' variant='outlined' label='Added on site' sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell><ResultChip result={c.result} /></TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{c.remark || '—'}</TableCell>
                    <TableCell><Thumbs files={c.photos} /></TableCell>
                  </TableRow>
                )
              )}
              {!v.checks.length && (
                <TableRow>
                  <TableCell {...({ colSpan: 5 } as object)} align='center'>No checklist items.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Card>
        <Stack direction='row' alignItems='center' gap={1} sx={{ mb: 1.5 }}>
          <Typography variant='subtitle1' fontWeight={600}>Site photos · {generalPhotos.length}</Typography>
          {v.canWork && <AddPhotoButton visitId={v.id} onAdded={load} />}
        </Stack>
        {generalPhotos.length ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 2 }}>
            {generalPhotos.map((p) => (
              <Box key={p.id}>
                <Box component='a' href={p.url} target='_blank' rel='noreferrer'>
                  <Box component='img' src={p.url} alt={p.caption ?? ''} loading='lazy'
                    sx={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider' }} />
                </Box>
                <Typography variant='caption' color='text.secondary'>{p.caption || dayjs(p.createdAt).format('DD MMM, hh:mm A')}</Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant='body2' color='text.secondary'>No site photos yet.</Typography>
        )}
      </Card>

      {v.canWork ? (
        <ReportEditor visit={v} onSaved={setVisit} />
      ) : (
        <Card>
          <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1.5 }}>Report</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Field label='Stage of work at site'>{v.workStage || '—'}</Field>
            <Field label='Persons met'>{v.personsMet || '—'}</Field>
            <Field label='Observations'><Box sx={{ whiteSpace: 'pre-wrap' }}>{v.observations || '—'}</Box></Field>
            <Field label='Instructions given at site'><Box sx={{ whiteSpace: 'pre-wrap' }}>{v.instructionsGiven || '—'}</Box></Field>
            <Field label='Outcome'>{v.outcome ? <OutcomeChip outcome={v.outcome} label={v.outcomeLabel} /> : '—'}</Field>
          </Box>
        </Card>
      )}

      <Dialog open={noteAction !== null} onClose={busy ? undefined : () => setNoteAction(null)} fullWidth maxWidth='sm'>
        {noteAction && (
          <>
            <DialogTitle>{NOTE_ACTIONS[noteAction].title}</DialogTitle>
            <DialogContent>
              <TextField autoFocus fullWidth multiline minRows={3} sx={{ mt: 1 }} label={NOTE_ACTIONS[noteAction].label}
                value={note} onChange={(e) => setNote(e.target.value)} />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setNoteAction(null)} disabled={busy}>Close</Button>
              <Button variant='contained' color={noteAction === 'cancel' ? 'error' : 'primary'} onClick={confirmNote}
                disabled={busy || (NOTE_ACTIONS[noteAction].required && !note.trim())}>
                {NOTE_ACTIONS[noteAction].button}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <ScheduleVisitDialog
        open={editing}
        meta={meta}
        visit={v}
        onClose={() => {
          setEditing(false);
          load();
        }}
        onSaved={(saved) => {
          setEditing(false);
          setVisit(saved);
        }}
      />

      <QuotationPdfViewer file={pdf} title={`Site visit ${v.visitNo}`}
        onClose={() => { if (pdfUrl.current) URL.revokeObjectURL(pdfUrl.current); pdfUrl.current = null; setPdf(null); }} />
    </Stack>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant='caption' color='text.secondary' display='block'>{label}</Typography>
      <Box sx={{ typography: 'body2' }}>{children}</Box>
    </Box>
  );
}

function Thumbs({ files }: { files: VisitFile[] }) {
  if (!files.length) return <>—</>;
  return (
    <Stack direction='row' gap={0.5} flexWrap='wrap'>
      {files.map((f) =>
        isImage(f) ? (
          <Box key={f.id} component='a' href={f.url} target='_blank' rel='noreferrer' title={f.caption ?? ''}>
            <Box component='img' src={f.url} alt={f.caption ?? ''} loading='lazy'
              sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 0.5, border: 1, borderColor: 'divider' }} />
          </Box>
        ) : (
          <Chip key={f.id} size='small' label={f.name ?? 'File'} component='a' href={f.url} target='_blank' rel='noreferrer' clickable />
        )
      )}
    </Stack>
  );
}

/** Uploads one photo onto the visit (or one checklist item) — the office filling in for the engineer. */
function AddPhotoButton({ visitId, checkId, onAdded }: { visitId: number; checkId?: number; onAdded: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const up = await uploadFile(file);
      await siteVisitsApi.addFile(visitId, { fileId: Number(up.id), kind: 'PHOTO', checkId: checkId ?? null });
      onAdded();
    } catch (e) {
      toast.error(logError({ message: 'Could not add the photo', error: e, code: 'SITE_VISIT_PHOTO_FAILED' }));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  return (
    <>
      <Button size='small' startIcon={busy ? <CircularProgress size={14} /> : <Icon icon='mdi:camera-plus-outline' />}
        disabled={busy} onClick={() => input.current?.click()}>
        Add photo
      </Button>
      <input ref={input} type='file' hidden accept='image/*' onChange={(e) => onFile(e.target.files?.[0])} />
    </>
  );
}

function EditableCheckRow({ index, visitId, check, onSaved }: { index: number; visitId: number; check: VisitCheck; onSaved: () => void }) {
  const [result, setResult] = useState<CheckResult | null>(check.result);
  const [remark, setRemark] = useState(check.remark ?? '');

  useEffect(() => {
    setResult(check.result);
    setRemark(check.remark ?? '');
  }, [check.result, check.remark]);

  const save = async (r: CheckResult | null, text: string) => {
    try {
      await siteVisitsApi.updateCheck(visitId, check.id, { result: r, remark: text.trim() || null });
      onSaved();
    } catch (e) {
      toast.error(logError({ message: 'Could not save the item', error: e, code: 'SITE_VISIT_CHECK_FAILED' }));
    }
  };

  return (
    <TableRow>
      <TableCell>{index + 1}</TableCell>
      <TableCell>
        <Typography variant='body2'>{check.title}</Typography>
        {check.hint && <Typography variant='caption' color='text.secondary'>{check.hint}</Typography>}
      </TableCell>
      <TableCell>
        <ToggleButtonGroup size='small' exclusive value={result}
          onChange={(_, r: CheckResult | null) => {
            setResult(r);
            save(r, remark);
          }}>
          <ToggleButton value='OK' color='success'>OK</ToggleButton>
          <ToggleButton value='ISSUE' color='error'>Issue</ToggleButton>
          <ToggleButton value='NA'>N/A</ToggleButton>
        </ToggleButtonGroup>
      </TableCell>
      <TableCell>
        <TextField size='small' fullWidth multiline value={remark} placeholder={result === 'ISSUE' ? 'Remark needed for an issue' : 'Remark'}
          error={result === 'ISSUE' && !remark.trim()}
          onChange={(e) => setRemark(e.target.value)}
          onBlur={() => remark !== (check.remark ?? '') && save(result, remark)} />
      </TableCell>
      <TableCell>
        <Stack gap={0.5}>
          <Thumbs files={check.photos} />
          <AddPhotoButton visitId={visitId} checkId={check.id} onAdded={onSaved} />
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function ReportEditor({ visit, onSaved }: { visit: VisitDetail; onSaved: (v: VisitDetail) => void }) {
  const [draft, setDraft] = useState<ReportDraft>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft({
      workStage: visit.workStage ?? '',
      personsMet: visit.personsMet ?? '',
      observations: visit.observations ?? '',
      instructionsGiven: visit.instructionsGiven ?? '',
      outcome: visit.outcome ?? '',
    });
  }, [visit]);

  const body = (): ReportDraft => ({ ...draft, outcome: draft.outcome || undefined });
  const set = (patch: ReportDraft) => setDraft((d) => ({ ...d, ...patch }));

  const save = async (submit: boolean) => {
    setBusy(true);
    try {
      const v = submit ? await siteVisitsApi.submit(visit.id, body()) : await siteVisitsApi.saveReport(visit.id, body());
      toast.success(submit ? 'Report submitted' : 'Report saved');
      onSaved(v);
    } catch (e) {
      toast.error(logError({ message: submit ? 'Could not submit the report' : 'Could not save the report', error: e, code: 'SITE_VISIT_REPORT_FAILED' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 0.5 }}>Report</Typography>
      <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 2 }}>
        Normally written by the engineer in the mobile app. Fill it here only on their behalf.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <TextField size='small' label='Stage of work at site' value={draft.workStage ?? ''} onChange={(e) => set({ workStage: e.target.value })} />
        <TextField size='small' label='Persons met' value={draft.personsMet ?? ''} onChange={(e) => set({ personsMet: e.target.value })} />
        <TextField size='small' label='Observations' multiline minRows={3} value={draft.observations ?? ''}
          onChange={(e) => set({ observations: e.target.value })} />
        <TextField size='small' label='Instructions given at site' multiline minRows={3} value={draft.instructionsGiven ?? ''}
          onChange={(e) => set({ instructionsGiven: e.target.value })} />
        <TextField select size='small' label='Outcome' value={draft.outcome ?? ''} onChange={(e) => set({ outcome: e.target.value as Outcome })}>
          <MenuItem value=''>Not decided</MenuItem>
          {OUTCOMES.map((o) => (
            <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>
          ))}
        </TextField>
      </Box>
      <Stack direction='row' gap={1} justifyContent='flex-end' sx={{ mt: 2 }}>
        <Button disabled={busy} onClick={() => save(false)}>Save draft</Button>
        <Button variant='contained' disabled={busy} onClick={() => save(true)}>Submit report</Button>
      </Stack>
    </Card>
  );
}
